import React, { useCallback, useMemo, useState } from 'react';
import styles from './TabLayout.module.scss';
import { MyCourseEnum } from '@types';
import { useMediaQuery } from 'react-responsive';
import { GettingStartedMode, CareGiverMode, IconInfo, IconShare, IconStandalone, IconMyCollection } from '@src/constants';
import { LordIcon } from '@src/components/lord-icon/lord-icon';
import Button from '@src/components/button/Button';
import IconOutline from 'src/assets/icons/new-outline-info.svg?react';
import { Tooltip } from 'antd';
import { useToast } from '@src/hooks';
import { useGenerateParentLinkForUserMutation } from '@src/api/graphql-codegen';
import { logger } from '../../../utils/logger';

const IconGetStarted = 'https://cdn.lordicon.com/kbdsgbei.json';
const IconBrainBreaks = 'https://cdn.lordicon.com/xpyyoyss.json';
const IconCaregiver = 'https://cdn.lordicon.com/aswgwjjg.json'

type Props = {
  studyMode: string;
  topPos: number;
  handleSelectPlanMode?: (studyMode: any) => void;
  isGettingStarted?: boolean;
  isCaregiver?: boolean;
  isUser?: boolean;
};

const getIconByType = (type: MyCourseEnum) => {
  switch (type) {
    case MyCourseEnum.Library:
      return IconMyCollection;
    case MyCourseEnum.Courses:
      return IconStandalone;
    default: {
      return IconStandalone;
    }
  }
}

function TabLayout(props: Props) {
  const { studyMode, topPos, handleSelectPlanMode, isGettingStarted, isCaregiver, isUser } = props;
  const middleWidth = useMediaQuery({maxWidth: 1330});
  const smallWidth = useMediaQuery({maxWidth: 1024});
  const [isGetStarted, setIsGetStarted] = useState(false);
  const [isBrainBreaks, setIsBrainBreaks] = useState(false);
  const [hoveredTab, setHoveredTab] = useState(0);

  const { successToast } = useToast();

  const [generateParentLinkForUser, { loading: generatingParentAppLink }] = useGenerateParentLinkForUserMutation();

  const isTablet = useMemo(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    return /(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/.test(userAgent)
  }, []);

  const _filtered = useMemo(() => {
    if (isGettingStarted) {
      return GettingStartedMode;
    }
    if (isCaregiver) {
      return CareGiverMode;
    }
    return (Object.keys(MyCourseEnum) as (keyof typeof MyCourseEnum)[]).map((key) => {
        return MyCourseEnum[key];
      },
    );
  }, [isCaregiver, isGettingStarted]);

  var height = middleWidth ? smallWidth ? 120 : 160 : 203;
  const leftScrollView = document.getElementById("navMenuBar")
  if (leftScrollView) {
    const rect = leftScrollView.getBoundingClientRect();
    if (topPos >= 88) {
      height = middleWidth ? smallWidth ? 120 : 160 : 203;
    } else if (rect.height - (87 - topPos) < 90) {
      height = 90;
    } else {
      height = rect.height - (87 - topPos);
    }
  }

  const generateAndCopyParentAppLink = useCallback(async () => {
    try {
      const result = await generateParentLinkForUser({ variables: { input: { uniqueName: '' } } });
      if (result.data?.generateParentLinkForUser?.id) {
        await navigator.clipboard.writeText(`${window.location.protocol}//${window.location.host}/parent/login?id=${result.data?.generateParentLinkForUser?.id}`)
        successToast('Link Copied Successfully!');
      }
    } catch (e) {
      logger.info(e)
    }
  }, [successToast, generateParentLinkForUser]);

  return (
    <div id={"navMenuBar"} className={`${studyMode === MyCourseEnum.Courses || isGettingStarted ? styles.studyModeNavView : styles.navView} ${isTablet ? '' : topPos < 88 ? styles.courseHeadStickyView : styles.headStickyView}`} style={{height: topPos < 88 ? '90px' : `${height}px`}}>
      {isGettingStarted ? <>
        <div className={styles.itemWrapper} onClick={() => handleSelectPlanMode?.(GettingStartedMode[0])} onMouseOver={() => setIsGetStarted(true)} onMouseLeave={() => setIsGetStarted(false)}>
          <div className={[styles.brainbreakItem, (studyMode === GettingStartedMode[0] && height > 100) ? styles.selItem : ''].join(' ')}>
            <LordIcon size={30} src={IconGetStarted} isHover={true} colors={{primary: studyMode === GettingStartedMode[0] ? '#FFB073' : isGetStarted ? '#FFB073' : '#FFFFFF', secondary: studyMode === GettingStartedMode[0] ? '#FFB073' : isGetStarted ? '#FFB073' : '#FFFFFF'}} target='.TabLayout_itemWrapper__nPLRI'/>
            <div className={[styles.listTitle, studyMode === GettingStartedMode[0] ? styles.selItemTitle : ''].join(' ')} style={{color: (isGetStarted || studyMode === GettingStartedMode[0]) ? '#FFB073' : '#FFFFFF'}}>
              {GettingStartedMode[0]}
            </div>
          </div>
        </div>
        <div className={styles.itemWrapper} onClick={() => handleSelectPlanMode?.(GettingStartedMode[1])} onMouseOver={() => setIsBrainBreaks(true)} onMouseLeave={() => setIsBrainBreaks(false)}>
          <div className={[styles.brainbreakItem, (studyMode === GettingStartedMode[1] && height > 100) ? styles.selItem : ''].join(' ')}>
            <LordIcon size={30} src={IconBrainBreaks} isHover={true} colors={{primary: studyMode === GettingStartedMode[1] ? '#FFB073' : isBrainBreaks ? '#FFB073' : '#FFFFFF', secondary: studyMode === GettingStartedMode[1] ? '#FFB073' : isBrainBreaks ? '#FFB073' : '#FFFFFF'}} target='.TabLayout_itemWrapper__nPLRI'/>
            <div className={[styles.listTitle, studyMode === GettingStartedMode[1] ? styles.selItemTitle : ''].join(' ')} style={{color: (isBrainBreaks || studyMode === GettingStartedMode[1]) ? '#FFB073' : '#FFFFFF'}}>
              {GettingStartedMode[1]}
            </div>
          </div>
        </div>
      </> : isCaregiver ?
        <div className={styles.itemWrapper}>
          <div className={[styles.caregiverItem, (height > 100) ? styles.selItem : ''].join(' ')}>
            <LordIcon size={30} src={IconCaregiver} isHover={true} colors={{primary: '#FFB073', secondary: '#FFB073' }} target='.TabLayout_itemWrapper__nPLRI'/>
            <div className={[styles.listTitle, styles.selItemTitle].join(' ')} style={{marginLeft: 10}}>
              {CareGiverMode[0]}
            </div>
          </div>
        </div>
      : _filtered.map((item, index) => {
        const Icon = getIconByType(item as MyCourseEnum);
        return (
          <div key={index} className={styles.itemWrapper} onClick={() => handleSelectPlanMode?.(item)} onMouseOver={() => setHoveredTab(index)} onMouseLeave={() => setHoveredTab(-1)}>
            <div className={[styles.listItem, (studyMode === item && height > 100) ? styles.selItem : ''].join(' ')}>
              <LordIcon size={30} src={Icon} isHover={true} colors={{primary: studyMode === item ? '#FFB073' : index === hoveredTab ? '#FFB073' : '#FFFFFF', secondary: studyMode === item ? '#FFB073' : index === hoveredTab ? '#FFB073' : '#FFFFFF'}} target='.TabLayout_itemWrapper__nPLRI'/>
              <div className={[styles.listTitle, studyMode === item ? styles.selItemTitle : ''].join(' ')}>
                {item}
              </div>
            </div>
          </div>
        )
      })}
      {(isCaregiver && isUser) &&
        <div className={styles.shareView}>
          <Button label='Share' isLoading={generatingParentAppLink} disabled={generatingParentAppLink} className={styles.shareButton} 
            leftIcon={<LordIcon src={IconShare} isHover={true} colors={{primary: '#FFFFFF', secondary: '#FFFFFF'}} target='.TabLayout_shareButton__fIDYe' size={30}/>} onClick={generateAndCopyParentAppLink} />
          <Tooltip
            placement='leftTop'
            title={
              <div className={styles.cardBack}>
                <IconOutline className={styles.hoverIcon} width={46}/>
                <div className={styles.hoverCardText}>
                  <div className={styles.span1}>Provide this link to your parents and caregivers so they can access these Lessons!</div>
                  <div className={styles.span2}><strong>Hint: </strong>Your school has been supplied with an Information Packet that they can also send out to families!</div>
                </div>
              </div>
            }
            overlayStyle={{maxWidth: '400px'}}
            showArrow={false}
            color='#FFFFFF'>
            <div className={styles.infoIcon} >
              <LordIcon size={30} src={IconInfo} isHover={true} colors={{primary: '#FFB073', secondary: '#FFB073'}}/>
            </div>
          </Tooltip>
        </div>
      }
    </div>
  );
}

export default TabLayout;
